import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, model, input, signal, ViewContainerRef, ViewChild, Injector, inject, OnDestroy, ComponentRef, untracked } from '@angular/core';
import { DinamikBilesenRegistry } from './dinamik-bilesen.registry';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { Subscription } from 'rxjs';

@Component({
  selector: 'dinamik-bilesen',
  standalone: true,
  imports: [CommonModule, NzSpinModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (yukleniyor() && !aktifVarMi()) {
      <nz-spin nzSimple></nz-spin>
    }
    <ng-container #hedef></ng-container>
    @if (hata()) {
      <div class="dinamik-bilesen-hata">{{ hata() }}</div>
    }
  `,
  styles: [`
    :host { display: contents; }
    .dinamik-bilesen-hata {
      color: var(--py-tehlike, #ff4d4f);
      padding: 8px;
      font-size: 12px;
    }
  `]
})
export class DinamikBilesenRendererComponent implements OnDestroy {
  bilesenId = input.required<string>();
  gorunum = input.required<string>();
  parametreler = model<any>();

  yukleniyor = signal(false);
  hata = signal<string | null>(null);
  aktifVarMi = signal(false);

  @ViewChild('hedef', { read: ViewContainerRef, static: true }) hedefRef!: ViewContainerRef;

  private injector = inject(Injector);
  private aktifRef: ComponentRef<any> | null = null;
  private compCache = new Map<string, ComponentRef<any>>();
  private cikisSub: Subscription | null = null;
  private yuklemeAbort: AbortController | null = null;
  /** Child → renderer güncelleme döngüsünü önlemek için guard */
  private _childGuncelliyorRendererı = false;

  private cacheKey = computed(() => `${this.bilesenId()}_${this.gorunum()}`);

  constructor() {
    effect(() => {
      const id = this.bilesenId();
      const gor = this.gorunum();
      const key = this.cacheKey();

      untracked(() => {
        if (!id || !gor) return;

        const mevcutKey = this.aktifRef ? `${this.aktifRef.instance?.['__bilesenId'] || ''}_${this.aktifRef.instance?.['__gorunum'] || ''}` : '';
        if (key === mevcutKey) {
          this.parametreleriGuncelle(this.parametreler());
          return;
        }

        this.bilesenYukle(id, gor, key);
      });
    });

    effect(() => {
      const params = this.parametreler();
      untracked(() => {
        // Child kendi güncellemesini yaptıysa renderer tekrar push etmez
        if (this._childGuncelliyorRendererı) return;
        if (this.aktifRef) this.parametreleriGuncelle(params);
      });
    });
  }

  private async bilesenYukle(id: string, gorunum: string, key: string) {
    this.yuklemeAbort?.abort();
    const abort = this.yuklemeAbort = new AbortController();

    this.yukleniyor.set(true);
    this.hata.set(null);

    try {
      const cached = this.compCache.get(key);
      if (cached) {
        if (cached.hostView.destroyed) {
          this.compCache.delete(key);
        } else {
          if (abort.signal.aborted) return;
          this.aktifRefDegistir(cached, id, gorunum);
          return;
        }
      }

      const comp = await DinamikBilesenRegistry.goruntumAl(id, gorunum);
      if (abort.signal.aborted) return;

      if (!comp) {
        this.hata.set(`'${id}' bileşeninde '${gorunum}' görünümü bulunamadı`);
        return;
      }

      const ref = this.hedefRef.createComponent(comp, { injector: this.injector });
      ref.instance['__bilesenId'] = id;
      ref.instance['__gorunum'] = gorunum;

      this.compCache.set(key, ref);
      this.aktifRefDegistir(ref, id, gorunum);
    } catch (e: any) {
      if (abort.signal.aborted) return;
      this.hata.set(`Bileşen yüklenemedi: ${e?.message || e}`);
    } finally {
      if (!abort.signal.aborted) this.yukleniyor.set(false);
    }
  }

  private aktifRefDegistir(ref: ComponentRef<any>, id: string, gorunum: string) {
    this.cikisSubTemizle();
    while (this.hedefRef.length > 0) this.hedefRef.detach(0);
    this.hedefRef.insert(ref.hostView);

    this.aktifRef = ref;
    this.aktifVarMi.set(true);
    this.parametreleriGuncelle(this.parametreler());
    this.cikislariBagla();
  }

  private parametreleriGuncelle(params: any) {
    if (!this.aktifRef) return;
    const instance = this.aktifRef.instance;

    if ('parametreler' in instance) {
      if (instance.parametreler?.set) {
        instance.parametreler.set(params);
      } else {
        this.aktifRef.setInput('parametreler', params);
      }
      return;
    }

    // 'parametreler' inputu yoksa anahtarları tekil input'lara eşle (HMI widget'ları)
    if (params && typeof params === 'object' && !Array.isArray(params)) {
      for (const [anahtar, deger] of Object.entries(params)) {
        if (anahtar in instance) {
          try { this.aktifRef.setInput(anahtar, deger); } catch { }
        }
      }
    }
  }

  private cikislariBagla() {
    if (!this.aktifRef) return;
    const instance = this.aktifRef.instance;

    // 1) EventEmitter tabanlı çıkış (eski desen: @Output parametrelerChange)
    if (instance.parametrelerChange?.subscribe) {
      this.cikisSub = instance.parametrelerChange.subscribe((yeniDeger: any) => {
        this.parametreler.set(yeniDeger);
      });
      return;
    }

    // 2) model() signal tabanlı çıkış (Angular 17+: model<any>() — OutputEmitterRef)
    // model() sinyali aynı zamanda bir WritableSignal'dir; değişimini effect() ile izleriz.
    if (typeof instance.parametreler === 'function' && instance.parametreler.set) {
      // effect() ile child signal'i izle, değişince renderer'ın model'ini güncelle
      const childEffect = effect(() => {
        const yeniDeger = instance.parametreler();
        untracked(() => {
          this._childGuncelliyorRendererı = true;
          this.parametreler.set(yeniDeger);
          this._childGuncelliyorRendererı = false;
        });
      }, { injector: this.injector, allowSignalWrites: true });
      // cleanup için Subscription benzeri bir sarmalayıcı oluştur
      this.cikisSub = { unsubscribe: () => childEffect.destroy() } as any;
    }
  }

  private cikisSubTemizle() {
    this.cikisSub?.unsubscribe();
    this.cikisSub = null;
  }

  ngOnDestroy() {
    this.yuklemeAbort?.abort();
    this.cikisSubTemizle();
    this.compCache.forEach(ref => {
      if (!ref.hostView.destroyed) ref.destroy();
    });
    this.compCache.clear();
    this.aktifRef = null;
  }
}
